import {handleNewPosts as handleNewPostsForSidebar} from 'features/sidebar/actions/handle_new_post';

import type {Post} from 'mattermost-redux/types/posts';
import store from 'stores/redux_store';
import {selectCurrentChannel} from 'mattermost-redux/selectors/entities/channels';
import {handlePostedEvents} from 'features/posts/actions/handle_new_post_events';
import {type PostedEvent, type PostWithMeta} from 'features/posts/types/posted_event';
import {receivedNewPost} from 'mattermost-redux/actions/posts';
import {WebsocketEvents} from 'mattermost-redux/constants';
import {actionsToMarkChannelAsUnread} from 'mattermost-redux/actions/channels';
import {getMentionsFromMessageProps} from 'features/notifications/actions/send_post_notification/utils';
import {getUserByUsername} from 'mattermost-redux/selectors/entities/users';
import {getProfilesByUsernames} from 'mattermost-redux/actions/users';
import {ActionTypes} from 'utils/constants';
import {isAppFocused} from 'features/app_activity/selector/is_app_focused';
import {parsePostedEvent} from 'features/posts/utils/parse_posted_event';
import {TimeLogger} from 'utils/logger';
import {normalizeError} from 'utils/normalize_error';
import {ChannelType} from '@mattermost/types/channels';
import {parseUsernamesFromPosts} from 'mattermost-redux/utils/parsePost';

const DEBOUNCE_TIMEOUT = 500;
const SYNC_CHANNEL_TIMEOUT = DEBOUNCE_TIMEOUT * 2;
const STACK_LIMIT = 50;
const THRESHOLD_STACKED = 10;

export const debounceHandlePostedEvent = (delay = DEBOUNCE_TIMEOUT) => {
    let timeoutId: NodeJS.Timeout;
    let stackedMessages: PostWithMeta[] = [];
    let handledMessagesCount = 0;
    let lastTimestamp: number;
    let lastDMLikeChannelId: Post['channel_id'];

    const flushMessages = () => {
        if (stackedMessages.length > 0) {
            store.dispatch(handlePostedEvents(stackedMessages));
        }

        stackedMessages = [];
    };

    const flushMessagesOnTimeout = () => {
        clearTimeout(timeoutId);

        flushMessages();

        handledMessagesCount = 0;
    };

    return (message: PostedEvent) => {
        let postWithMeta: PostWithMeta;

        try {
            const parsedMessage = parsePostedEvent(message);
            postWithMeta = {post: parsedMessage.data.post, meta: message.data};
        } catch (error) {
            TimeLogger.error({
                error: normalizeError(error),
                message: 'Failed to parse posted event',
            });

            return;
        }

        const state = store.getState();
        const currentChannel = selectCurrentChannel(state);
        const post = postWithMeta.post;

        const shouldHandleEventSeparately = currentChannel && currentChannel.id === post.channel_id && (
            currentChannel.type === ChannelType.Direct || currentChannel.type === ChannelType.Group
        );
        if (shouldHandleEventSeparately) {
            const shouldSyncChannel = !lastTimestamp || (
                post.create_at - lastTimestamp > SYNC_CHANNEL_TIMEOUT &&
                Boolean(lastDMLikeChannelId) && lastDMLikeChannelId === post.channel_id
            );

            lastTimestamp = post.create_at;
            lastDMLikeChannelId = post.channel_id;

            if (shouldSyncChannel) {
                return store.dispatch(handlePostedEvents([postWithMeta]));
            }

            const usernamesFromPostsTextAndAttachments = parseUsernamesFromPosts([post]);
            const usernamesToLoad = usernamesFromPostsTextAndAttachments.filter(
                (username) => !getUserByUsername(state, username),
            );

            if (usernamesToLoad.length > 0) {
                store.dispatch(getProfilesByUsernames(Array.from(usernamesToLoad)));
            }

            // Может возникнуть ситуация, когда нам написали
            // из канала, которого нет в channel_ids категории
            // (если включена опция фильтрации на бэке)
            store.dispatch(handleNewPostsForSidebar([post]));

            store.dispatch({
                type: ActionTypes.INCREASE_POST_VISIBILITY,
                data: post.channel_id,
                amount: 1,
            });
            store.dispatch(receivedNewPost(post, true));
            store.dispatch({
                type: WebsocketEvents.STOP_TYPING,
                data: {
                    id: post.channel_id + post.root_id,
                    userId: post.user_id,
                    now: Date.now(),
                },
            });

            if (!isAppFocused(store.getState())) {
                actionsToMarkChannelAsUnread(
                    store.getState,
                    currentChannel.team_id,
                    post.channel_id,
                    getMentionsFromMessageProps(message.data),
                    false,
                    post.root_id === '',
                ).forEach((action) => store.dispatch(action));
            }

            return;
        }

        if (timeoutId && handledMessagesCount > THRESHOLD_STACKED) {
            if (stackedMessages.push(postWithMeta) > STACK_LIMIT) {
                flushMessages();
                return;
            }

            clearTimeout(timeoutId);
            timeoutId = setTimeout(flushMessagesOnTimeout, delay);
            return;
        }

        handledMessagesCount += 1;
        store.dispatch(handlePostedEvents([postWithMeta]));
        clearTimeout(timeoutId);
        timeoutId = setTimeout(flushMessagesOnTimeout, delay);
    };
};

export const handlePostedEventDebounced = debounceHandlePostedEvent();
